import React from 'react'
import Select, { components, MenuListProps, SingleValue } from 'react-select'
import styles from './TemplateSelect.module.css'
import { useMediaQuery } from 'react-responsive'
import plusIcon from '../../assets/icons/plusIcon.svg'
import iconEdit from '../../assets/icons/iconEdit.svg'
import IconTrashRed from '../../assets/icons/IconTrashRed.svg'
import { CommentTemplateType } from '../../redux/actions/templatesAction'

type OptionType = {
  value: number
  label: string
  template: CommentTemplateType
}

type TemplateSelectProps = {
  templates: CommentTemplateType[]
  value?: number
  onChange: (template: CommentTemplateType) => void
  onAdd: () => void
  onEdit: (template: CommentTemplateType) => void
  onDelete: (templateId: number) => void
  placeholder?: string
  isDisabled?: boolean
}

const TemplateSelect: React.FC<TemplateSelectProps> = ({
  templates,
  value,
  onChange,
  onAdd,
  onEdit,
  onDelete,
  placeholder = 'Выберите шаблон',
  isDisabled = false,
}) => {
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  const options: OptionType[] = templates.map((template) => ({
    value: template.id,
    label: template.name,
    template,
  }))

  const selectedOption = options.find((opt) => opt.value === value) || null

  const MenuList = (props: MenuListProps<OptionType>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div className={styles.addTemplateButton} onClick={onAdd}>
          <img src={plusIcon} alt='add' />
          <span>Добавить шаблон</span>
        </div>
      </components.MenuList>
    )
  }

  const Option = (props: any) => {
    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      onEdit(props.data.template)
    }

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation()
      onDelete(props.data.template.id)
    }

    return (
      <components.Option {...props}>
        <div className={styles.optionContent}>
          <span className={styles.optionLabel}>{props.data.label}</span>
          <div className={styles.optionActions}>
            <button
              type='button'
              className={styles.iconButton}
              onClick={handleEdit}
              title='Редактировать'
            >
              <img src={iconEdit} alt='edit' />
            </button>
            <button
              type='button'
              className={styles.iconButton}
              onClick={handleDelete}
              title='Удалить'
            >
              <img src={IconTrashRed} alt='delete' />
            </button>
          </div>
        </div>
      </components.Option>
    )
  }

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      border: 'none',
      boxShadow: 'none',
      minHeight: 'auto',
      backgroundColor: 'transparent',
    }),
    menu: (provided: any) => ({
      ...provided,
      right: 0,
      overflow: 'auto',
      zIndex: 1000,
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '250px',
      overflowY: 'auto',
      padding: 0,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      padding: '8px 12px',
      backgroundColor: state.isSelected ? 'rgba(20, 25, 26, 0.08)' : 'white',
      color: '#000',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(20, 25, 26, 0.04)',
      },
    }),
    input: (provided: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
      height: isMobile ? 44 : 24,
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: 0,
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: 0,
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  }

  const handleChange = (newValue: SingleValue<OptionType>) => {
    if (newValue) {
      onChange(newValue.template)
    }
  }

  return (
    <div className={styles.templateSelect}>
      <Select<OptionType>
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        styles={customStyles}
        isDisabled={isDisabled}
        menuPortalTarget={document.body}
        menuPosition='fixed'
        components={{ MenuList, Option }}
      />
    </div>
  )
}

export default TemplateSelect
